import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Beacon de auditoria de acesso (P0 — ver AGENTS/tarefa do Shell).
 *
 * O middleware `_access_audit` do backend (`app/main.py`) só grava uma
 * visita de página quando o path é `/` ou termina em `.html` sob `/ui/`.
 * Isso fazia sentido no legado (cada navegação era uma requisição real de
 * documento), mas neste SPA a navegação entre rotas é 100% client-side —
 * `react-router` troca a tela via `history.pushState`, sem nenhum GET novo
 * para o servidor. Sem isto, a tela de Auditoria de acesso perderia todo o
 * histórico de navegação dentro do painel novo, o que equivale a remover
 * uma funcionalidade existente (proibido nesta refatoração).
 *
 * A correção: a cada troca de rota, disparamos um GET best-effort (sem
 * `await`, sem bloquear render, erro ignorado) para o arquivo `.html`
 * equivalente sob `/ui/` — o mesmo arquivo que `scripts/emit-routes.mjs`
 * materializa no build. O middleware grava a visita normalmente; o corpo da
 * resposta é descartado, não usamos o resultado pra nada.
 *
 * Não dispara para `/login` (tela pública, sem sessão — nada relevante de
 * auditoria "de organizador logado" acontece nela, e ela já é coberta pelo
 * acesso direto ao arquivo físico `/ui/login.html` quando o middleware de
 * auth redireciona pra lá).
 */
export function usePageViewBeacon(): void {
  const location = useLocation();

  useEffect(() => {
    const slug = location.pathname.replace(/^\/+|\/+$/g, "");
    if (!slug || slug === "login") return;

    const controller = new AbortController();
    fetch(`/ui/${slug}.html`, {
      method: "GET",
      credentials: "same-origin",
      signal: controller.signal,
    }).catch(() => {
      // best-effort: uma falha aqui nunca deve afetar a navegação real.
    });

    return () => controller.abort();
  }, [location.pathname]);
}
